import { useAutoAnimate } from '@formkit/auto-animate/react'
import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import LayoutTwoColumnPage from 'src/component-layout/LayoutTwoColumnPage/LayoutTwoColumnPage'
import DepartmentDetailsComponent, {
  DepartmentDetailsFields,
} from 'src/components/DepartmentDetails/DepartmentDetailsComponent'
import DepartmentMembersTable from 'src/components/DepartmentMembersTable/DepartmentMembersTable'
import DynamicSuspenseContainer from 'src/components/DynamicSuspenseContainer/DynamicSuspenseContainer'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import LoadingShimmer from 'src/components/LoadingShimmer/LoadingShimmer'
import SvgChevronDown from 'src/components/Svgs/SvgChevronDown'
import SvgTrash from 'src/components/Svgs/SvgTrash'
import { useTranslate } from 'src/localization/Trns'
import {
  useDeleteDepartment,
  useQueryDepartmentDetails,
} from 'src/queries/organizations/department.queries'
import {
  selectDepartmentIdFromRoute,
  selectOrganizationIdFromRoute,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import styled, { css } from 'styled-components'

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`
const TableTitle = styled.span`
  ${getFontCss(
    20,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.medium,
  )}
`

const DepartmentDetailsPage = (): React.ReactElement => {
  const organizationId = useSelector(selectOrganizationIdFromRoute)
  const departmentId = useSelector(selectDepartmentIdFromRoute)
  const appName = useSelector(selectAppName)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [dropdownRef] = useAutoAnimate({ duration: 100, easing: 'ease-in' })
  const { mutate: deleteDepartment } = useDeleteDepartment(
    organizationId,
    departmentId,
  )
  const { analytics } = useAnalyticsContext()
  const translate = useTranslate()

  const listItem = (): React.ReactElement => (
    <HoverableLink
      css={css`
        display: flex;
        white-space: wrap;
        padding: 12px 20px;
        ${getFontCss(
          14,
          styleConstants.colors.red_dark,
          styleConstants.fontFamilies.regular,
        )};
        align-items: start;
      `}
      ariaLabelSource={'Delete Team'}
      onClick={(): void => {
        deleteDepartment({ organizationId, departmentId })
        analytics.track('departments - delete department')
      }}
    >
      <div
        css={css`
          display: flex;
          margin-right: 5px;
        `}
      >
        <SvgTrash />
      </div>
      <div>
        <div
          css={css`
            margin-top: 5px;
            ${getFontCss(
              14,
              styleConstants.colors.red_dark,
              styleConstants.fontFamilies.regular,
            )};
            &:hover {
              text-decoration: underline;
            }
          `}
        >
          {translate('Delete')}
        </div>
        <div
          css={css`
            ${getFontCss(
              12,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          {translate('Permanently delete this team from your organization')}
        </div>
      </div>
    </HoverableLink>
  )

  const loadingDetailsFallback = (
    <>
      <FlexColumn
        css={css`
          gap: 20px;
        `}
      >
        <FlexColumn
          css={css`
            gap: 8px;
          `}
        >
          <LoadingShimmer width={'100%'} />
          <LoadingShimmer width={'100%'} />
        </FlexColumn>
        <FlexColumn>
          <DepartmentDetailsFields>
            <LoadingShimmer width={'100%'} />
            <LoadingShimmer width={'100%'} />
          </DepartmentDetailsFields>
          <DepartmentDetailsFields>
            <LoadingShimmer width={'100%'} />
            <LoadingShimmer width={'100%'} />
          </DepartmentDetailsFields>
        </FlexColumn>
      </FlexColumn>
    </>
  )

  const loadingListFallback = (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 16px;
        width: 100%;
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          align-self: stretch;
        `}
      >
        <TableTitle>{translate('Members')}</TableTitle>
      </div>
      <LoadingShimmer width={'100%'} />
      <LoadingShimmer width={'100%'} />
      <LoadingShimmer width={'100%'} />
      <LoadingShimmer width={'100%'} />
    </div>
  )

  const actionComponent = (
    <div
      css={css`
        margin-left: auto;
        ${getBreakpointDown(breakpointSizes.XSM)(css`
          margin-right: 12px;
        `)}
      `}
      onMouseEnter={(): void => {
        setIsHovered(true)
        setDropdownOpen(true)
      }}
      onMouseLeave={(): void => {
        setIsHovered(false)
        setDropdownOpen(false)
      }}
    >
      <HoverableLink
        css={css`
          display: flex;
          justify-content: flex-end;
          align-items: center;
        `}
        ariaLabelSource={'Team Options'}
        testId={'genericLayoutDetailsUpdateBtn'}
        onClick={(): void => {
          if (!isHovered) {
            setDropdownOpen(!dropdownOpen)
          }
        }}
        size={HoverableLinkSizes.Medium}
      >
        {translate('Team Options')}
        <SvgChevronDown
          css={css`
            transform: scale(calc(14 / 24));
          `}
        />
      </HoverableLink>
      <div ref={dropdownRef}>
        {dropdownOpen && (
          <div
            css={css`
              z-index: 1;
              position: absolute;
              right: 0px;
              border-radius: 10px;
              display: flex;
              flex-direction: column;
              border: 1px solid ${styleConstants.colors.gray_medium};
              box-shadow: 0 3px 10px 0 rgba(0, 0, 0, 0.1);
              width: 250px;
              background-color: ${styleConstants.colors.white};
              gap: 8px;
            `}
          >
            {listItem()}
          </div>
        )}
      </div>
    </div>
  )

  const { data: departmentData } = useQueryDepartmentDetails({
    organizationId,
    departmentId,
  })

  return (
    <>
      <Helmet>
        <title>{`${departmentData?.name ?? '--'} | ${appName}`}</title>
        <meta
          name="description"
          content={`departmentData?.name ?? '--' | ${appName}`}
        />
      </Helmet>
      <LayoutPage padding={'medium'}>
        <LayoutTwoColumnPage
          detailsComponent={
            <DynamicSuspenseContainer fallback={loadingDetailsFallback}>
              <DepartmentDetailsComponent />
            </DynamicSuspenseContainer>
          }
          listComponent={
            <DynamicSuspenseContainer fallback={loadingListFallback}>
              <div className={'flex-column gap-6 w-full'}>
                <div className={'flex-row gap-8 justify-between'}>
                  <div className={'font-h3'}>{translate('Members')}</div>
                  {actionComponent}
                </div>
                <DepartmentMembersTable
                  departmentId={departmentId}
                  departmentName={departmentData?.name}
                  organizationId={organizationId}
                />
              </div>
            </DynamicSuspenseContainer>
          }
        />
      </LayoutPage>
    </>
  )
}

export default DepartmentDetailsPage
